// Checklist do que a empresa DEVE manter (2026-08-19).
//
// O acervo responde "o que eu tenho". Esta tela responde "o que está faltando" —
// que é a pergunta na véspera do pregão e no dia em que a CND vence.
//
// Duas decisões que moldam a tela:
//
//  1. Linha "faltando" não é acusação, é trabalho. Quando o documento existe no
//     acervo mas está sem subcategoria (20 dos 47 em 19/08/2026), a linha mostra
//     "N em <categoria> sem subcategoria" e leva para lá — porque o mais comum é
//     o documento já estar lá, só mal classificado.
//  2. O botão "Anexar" abre o modal já amarrado ao item (esperado_id). Sem esse
//     vínculo explícito, item de instância (o CRLV de UM veículo) nunca fecha:
//     o casamento automático só vale para documento de empresa.
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button, Card, Table, Tag, Typography, Flex, Space, Tooltip, Empty, Alert,
  Modal, Input, Select, Switch, InputNumber, App as AntApp,
} from 'antd';
import { PlusOutlined, PaperClipOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { fmtDataBr } from '../../utils/dataFmt';
import { Se } from '../../components/Se';
import type { CategoriaDoc, SubcategoriaDoc, Esperado, ResumoChecklist, SituacaoEsperado } from './types';
import {
  listarEsperados, salvarEsperado, excluirEsperado,
  listarCategorias, listarSubcategorias, listarDocumentos, atualizarDocumento,
} from './api';
import type { Documento } from './types';
import { ModalDocumento } from './ModalDocumento';

const { Text, Title } = Typography;

const SIT: Record<SituacaoEsperado, { label: string; cor: string; ajuda: string }> = {
  faltando:     { label: 'Faltando',     cor: 'red',     ajuda: 'Nenhum documento do acervo atende este item' },
  incompleto:   { label: 'Incompleto',   cor: 'orange',  ajuda: 'Tem documento, mas menos que a quantidade esperada' },
  vencido:      { label: 'Vencido',      cor: 'red',     ajuda: 'A validade do documento já passou' },
  vencendo:     { label: 'Vencendo',     cor: 'gold',    ajuda: 'Vence dentro do prazo de alerta deste item' },
  sem_validade: { label: 'Sem validade', cor: 'purple',  ajuda: 'O item exige vigência e o documento não tem data — pendência de cadastro, não de documento' },
  ok:           { label: 'OK',           cor: 'green',   ajuda: 'Documento presente e vigente' },
};
// Ordem dos cards = ordem de urgência. "OK" por último, de propósito.
const CARDS: SituacaoEsperado[] = ['faltando', 'vencido', 'vencendo', 'incompleto', 'sem_validade', 'ok'];

export function ChecklistEsperados({ onIrParaAcervo, onAcervoMudou }: {
  /** Leva para a aba do acervo já filtrada — usado pela pista "N sem subcategoria". */
  onIrParaAcervo?: (filtro: { categoria_codigo: string }) => void;
  /** Anexar aqui cria documento no Acervo; a outra aba fica montada e velha se
   *  ninguém avisar. */
  onAcervoMudou?: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [itens, setItens] = useState<Esperado[]>([]);
  const [resumo, setResumo] = useState<ResumoChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroSit, setFiltroSit] = useState<SituacaoEsperado | null>(null);
  const [categorias, setCategorias] = useState<CategoriaDoc[]>([]);
  const [subcategorias, setSubcategorias] = useState<SubcategoriaDoc[]>([]);
  const [anexando, setAnexando] = useState<Esperado | null>(null);
  const [editando, setEditando] = useState<Partial<Esperado> | null>(null);
  const [classificando, setClassificando] = useState<Esperado | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listarEsperados(filtroSit ? { situacao: filtroSit } : {});
      setItens(r.esperados || []);
      setResumo(r.resumo || null);
      setErro(null);
    } catch (e: any) {
      setItens([]);
      const st = e?.response?.status;
      setErro(st === 403
        ? 'Você não tem permissão para ver o checklist de documentos da empresa.'
        : 'Não foi possível carregar o checklist.');
    } finally { setLoading(false); }
  }, [filtroSit]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([listarCategorias(), listarSubcategorias()]);
        setCategorias(c.categorias || []); setSubcategorias(s.subcategorias || []);
      } catch { /* o checklist funciona sem isso; só o formulário fica sem opções */ }
    })();
  }, []);

  const porCategoria = useMemo(() => {
    const m = new Map<string, Esperado[]>();
    for (const i of itens) {
      const k = i.categoria_nome;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(i);
    }
    // Array.from, não [...m.entries()]: o tsconfig do projeto tem target ES5 sem
    // downlevelIteration, e o spread de iterador não compila (TS2802).
    return Array.from(m.entries());
  }, [itens]);

  async function excluir(item: Esperado) {
    modal.confirm({
      title: `Remover "${item.nome}" do checklist?`,
      content: 'O documento vinculado continua no acervo — sai apenas a linha da lista de obrigações.',
      okText: 'Remover', okButtonProps: { danger: true }, cancelText: 'Cancelar',
      onOk: async () => {
        try { await excluirEsperado(item.id); message.success('Item removido.'); carregar(); }
        catch (e: any) { message.error('Não foi possível remover: ' + (e?.response?.data?.error || e.message)); }
      },
    });
  }

  const colunas: ColumnsType<Esperado> = [
    {
      title: 'Situação', dataIndex: 'situacao', key: 'sit', width: 130,
      render: (v: SituacaoEsperado) => (
        <Tooltip title={SIT[v]?.ajuda}><Tag color={SIT[v]?.cor}>{SIT[v]?.label || v}</Tag></Tooltip>
      ),
    },
    {
      title: 'Item', dataIndex: 'nome', key: 'nome',
      render: (v, row) => (
        <div>
          <Space size={6} wrap>
            <Text strong>{v}</Text>
            {!row.obrigatorio && <Tag>opcional</Tag>}
            {row.escopo === 'instancia' && <Tag color="blue">{row.chave_instancia}</Tag>}
          </Space>
          {row.subcategoria_nome && (
            <div style={{ fontSize: 10, color: '#666' }}>{row.categoria_nome} › {row.subcategoria_nome}</div>
          )}
          {/* Onde se emite. Só aparece no que exige ação — no item "ok" seria
              ruído, porque não há o que renovar hoje. `rel="noreferrer"` porque
              é link para fora: sem ele a página do órgão recebe o endereço da
              tela interna no Referer. */}
          {row.url_emissao && row.situacao !== 'ok' && (
            <div style={{ fontSize: 11, marginTop: 2 }}>
              <a href={row.url_emissao} target="_blank" rel="noreferrer">
                emitir{row.orgao ? ` — ${row.orgao}` : ''} ↗
              </a>
            </div>
          )}
          {row.situacao === 'faltando' && row.candidatos_sem_subcategoria > 0 && (
            <div style={{ fontSize: 11, marginTop: 3 }}>
              <Text type="warning">
                {row.candidatos_sem_subcategoria} documento{row.candidatos_sem_subcategoria > 1 ? 's' : ''} em {row.categoria_nome} sem subcategoria
              </Text>
              {/* O caminho curto: em vez de mandar a pessoa caçar no acervo,
                  mostra os candidatos aqui e classifica com um clique. E
                  classificar CORRIGE o documento (ele passa a dizer o que é),
                  não só amarra o item — por isso é melhor que um vínculo. */}
              <Se permissao="administrativo.documentos_empresa.editar">
                <Button type="link" size="small" style={{ padding: '0 6px' }}
                  onClick={() => setClassificando(row)}>
                  escolher qual é
                </Button>
              </Se>
              {onIrParaAcervo && (
                <Button type="link" size="small" style={{ padding: '0 6px' }}
                  onClick={() => onIrParaAcervo({ categoria_codigo: row.categoria_codigo })}>
                  ver no acervo
                </Button>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Documento que atende', dataIndex: 'doc_titulo', key: 'doc', width: 280,
      render: (v, row) => v
        ? (
          <div>
            <Text style={{ fontSize: 12 }}>{v}</Text>
            <div style={{ fontSize: 10, color: '#666' }}>
              {row.doc_codigo}{row.explicito ? ' · vinculado à mão' : ' · casou por categoria'}
            </div>
          </div>
        )
        : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    {
      title: 'Validade', dataIndex: 'data_validade', key: 'val', width: 110,
      render: (v, row) => v
        ? fmtDataBr(v)
        : <Text type="secondary" style={{ fontSize: 12 }}>{row.exige_validade ? 'não informada' : 'permanente'}</Text>,
    },
    {
      title: '', key: 'acoes', width: 190, align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Se permissao="administrativo.documentos_empresa.criar">
            <Button size="small" icon={<PaperClipOutlined />} onClick={() => setAnexando(row)}>
              {row.situacao === 'faltando' ? 'Anexar' : 'Substituir'}
            </Button>
          </Se>
          <Se permissao="administrativo.documentos_empresa.esperados.gerenciar">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditando(row)} />
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => excluir(row)} />
          </Se>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Flex gap={8} wrap style={{ marginBottom: 12 }}>
        {CARDS.map((s) => {
          const n = resumo?.[s] ?? 0;
          const ativo = filtroSit === s;
          return (
            <Card key={s} size="small" hoverable
              onClick={() => setFiltroSit(ativo ? null : s)}
              style={{
                minWidth: 118, cursor: 'pointer',
                borderColor: ativo ? '#1677ff' : undefined,
                opacity: n === 0 && !ativo ? 0.55 : 1,
              }}
              styles={{ body: { padding: '8px 12px' } }}>
              <Text type="secondary" style={{ fontSize: 11 }}>{SIT[s].label}</Text>
              <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.1 }}>{n}</div>
            </Card>
          );
        })}
        <div style={{ flex: 1 }} />
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>Atualizar</Button>
          <Se permissao="administrativo.documentos_empresa.esperados.gerenciar">
            <Button size="small" type="primary" icon={<PlusOutlined />}
              onClick={() => setEditando({ escopo: 'empresa', obrigatorio: true, alerta_dias: 30, quantidade_esperada: 1 })}>
              Novo item
            </Button>
          </Se>
        </Space>
      </Flex>

      {filtroSit && (
        <Alert type="info" showIcon style={{ marginBottom: 10 }}
          message={`Mostrando só os itens com situação "${SIT[filtroSit].label}". Os números dos cards continuam sendo do checklist inteiro.`}
          action={<Button size="small" onClick={() => setFiltroSit(null)}>Ver todos</Button>} />
      )}

      {erro && <Alert type="warning" showIcon message={erro} style={{ marginBottom: 12 }} />}

      {!erro && !loading && itens.length === 0 && (
        <Empty description={filtroSit ? 'Nenhum item nessa situação' : 'Checklist vazio — cadastre o que a empresa precisa manter'} />
      )}

      {porCategoria.map(([nome, linhas]) => (
        <div key={nome} style={{ marginBottom: 18 }}>
          <Title level={5} style={{ margin: '0 0 6px' }}>{nome}</Title>
          <Table<Esperado>
            rowKey="id" size="small" columns={colunas} dataSource={linhas}
            loading={loading} pagination={false} />
        </div>
      ))}

      {anexando && (
        <ModalDocumento
          documento={null} categorias={categorias} subcategorias={subcategorias} todosDocs={[]}
          paraEsperado={{
            id: anexando.id, nome: anexando.nome,
            categoria_codigo: anexando.categoria_codigo,
            subcategoria_codigo: anexando.subcategoria_codigo,
          }}
          onClose={() => setAnexando(null)}
          onSaved={() => { setAnexando(null); carregar(); onAcervoMudou?.(); }} />
      )}

      {editando && (
        <ModalItemEsperado item={editando} categorias={categorias} subcategorias={subcategorias}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); carregar(); }} />
      )}

      {classificando && (
        <ModalClassificarCandidato item={classificando}
          onClose={() => setClassificando(null)}
          onSaved={() => { setClassificando(null); carregar(); onAcervoMudou?.(); }} />
      )}
    </div>
  );
}

// ── "Escolher qual é": classifica um documento que já está no acervo ───────
//
// O caso real (20/08/2026): 13 itens do checklist aparecem como FALTANDO, e o
// documento de quase todos já está no acervo — só não diz o que é, porque veio
// sem subcategoria. O caminho antigo era ir ao Acervo, filtrar a categoria,
// abrir um a um e adivinhar. Aqui a pessoa vê só os candidatos daquele item e
// resolve com um clique.
//
// Classificar > vincular: gravar a subcategoria conserta o DOCUMENTO (ele passa
// a dizer o que é, e serve para busca, para o Dwight e para o próximo item),
// enquanto um vínculo explícito só amarraria aquele par.
function ModalClassificarCandidato({ item, onClose, onSaved }: {
  item: Esperado;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = AntApp.useApp();
  const [candidatos, setCandidatos] = useState<Documento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await listarDocumentos({ categoria_codigo: item.categoria_codigo });
        // Só os que ainda não dizem o que são: os já classificados não são
        // candidatos deste item (ou já teriam casado).
        setCandidatos((r.documentos || []).filter((d) => !d.subcategoria_codigo));
      } catch (e: any) {
        message.error('Não foi possível listar os candidatos: ' + (e?.response?.data?.error || e.message));
      } finally { setCarregando(false); }
    })();
  }, [item.categoria_codigo, message]);

  async function escolher(doc: Documento) {
    if (!item.subcategoria_codigo) {
      message.warning('Este item não tem subcategoria definida — edite o item antes.');
      return;
    }
    setSalvando(doc.id);
    try {
      await atualizarDocumento({ id: doc.id, subcategoria_codigo: item.subcategoria_codigo });
      message.success(`"${doc.titulo}" agora é ${item.subcategoria_nome}.`);
      onSaved();
    } catch (e: any) {
      message.error('Não foi possível classificar: ' + (e?.response?.data?.error || e.message));
    } finally { setSalvando(null); }
  }

  return (
    <Modal open onCancel={onClose} footer={[<Button key="f" onClick={onClose}>Fechar</Button>]}
      title={`Qual destes é "${item.nome}"?`}
      width="94vw" style={{ maxWidth: 760, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' } }} destroyOnClose>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message={`Documentos em ${item.categoria_nome} que ainda não dizem o que são`}
        description={`Ao escolher, o documento passa a ser "${item.subcategoria_nome || '—'}" e o item fecha sozinho. Isso conserta o cadastro, não só o checklist.`} />
      {carregando && <Text type="secondary">Carregando…</Text>}
      {!carregando && candidatos.length === 0 && (
        <Empty description="Nenhum documento sem subcategoria nesta categoria. Use Anexar para subir o documento." />
      )}
      <Flex vertical gap={8}>
        {candidatos.map((d) => (
          <Card key={d.id} size="small" styles={{ body: { padding: 10 } }}>
            <Flex justify="space-between" align="center" gap={10}>
              <div style={{ minWidth: 0 }}>
                <Text strong style={{ fontSize: 13 }}>{d.titulo}</Text>
                <div style={{ fontSize: 11, color: '#888' }}>
                  {d.codigo}
                  {d.emitente ? ` · ${d.emitente}` : ''}
                  {d.data_validade ? ` · vence ${fmtDataBr(d.data_validade)}` : ''}
                </div>
              </div>
              <Button size="small" type="primary" loading={salvando === d.id}
                onClick={() => escolher(d)}>É este</Button>
            </Flex>
          </Card>
        ))}
      </Flex>
    </Modal>
  );
}

// ── Formulário do item do checklist ────────────────────────────────────────
function ModalItemEsperado({ item, categorias, subcategorias, onClose, onSaved }: {
  item: Partial<Esperado>;
  categorias: CategoriaDoc[];
  subcategorias: SubcategoriaDoc[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = AntApp.useApp();
  const [f, setF] = useState({
    nome: item.nome || '',
    descricao: item.descricao || '',
    categoria_codigo: item.categoria_codigo || (categorias[0]?.codigo ?? ''),
    subcategoria_codigo: item.subcategoria_codigo || '',
    escopo: (item.escopo || 'empresa') as 'empresa' | 'instancia',
    chave_instancia: item.chave_instancia || '',
    quantidade_esperada: item.quantidade_esperada ?? 1,
    obrigatorio: item.obrigatorio ?? true,
    exige_validade: item.exige_validade ?? false,
    alerta_dias: item.alerta_dias ?? 30,
  });
  const [salvando, setSalvando] = useState(false);
  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }));
  const subs = subcategorias.filter((s) => s.categoria_codigo === f.categoria_codigo);

  async function salvar() {
    if (!f.nome.trim()) { message.warning('Dê um nome ao item'); return; }
    if (f.escopo === 'instancia' && !f.chave_instancia.trim()) {
      message.warning('No escopo "por instância", diga de qual veículo/sócio/imóvel é este item');
      return;
    }
    setSalvando(true);
    try {
      await salvarEsperado({ id: item.id, ...f, subcategoria_codigo: f.subcategoria_codigo || null });
      message.success(item.id ? 'Item atualizado.' : 'Item criado.');
      onSaved();
    } catch (e: any) {
      message.error('Não foi possível salvar: ' + (e?.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} onOk={salvar} confirmLoading={salvando}
      title={item.id ? 'Editar item do checklist' : 'Novo item do checklist'}
      okText="Salvar" cancelText="Cancelar"
      width="94vw" style={{ maxWidth: 620, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }} destroyOnClose>
      <Flex vertical gap={10} style={{ marginTop: 10 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Nome do item</Text>
          <Input value={f.nome} onChange={(e) => set('nome', e.target.value)} placeholder="CND Federal (RFB/PGFN)" />
        </div>
        <Flex gap={8} wrap>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Categoria</Text>
            <Select style={{ width: '100%' }} value={f.categoria_codigo}
              onChange={(v) => { set('categoria_codigo', v); set('subcategoria_codigo', ''); }}
              options={categorias.map((c) => ({ label: c.restrita ? `🔒 ${c.nome}` : c.nome, value: c.codigo }))} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Subcategoria</Text>
            <Select style={{ width: '100%' }} value={f.subcategoria_codigo || undefined} allowClear
              placeholder="(qualquer uma da categoria)"
              onChange={(v) => set('subcategoria_codigo', v || '')}
              options={subs.map((s) => ({ label: s.nome, value: s.codigo }))} />
          </div>
        </Flex>
        <Flex gap={8} wrap align="flex-end">
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Escopo</Text>
            <Select style={{ width: '100%' }} value={f.escopo}
              onChange={(v) => set('escopo', v)}
              options={[
                { label: 'Um por empresa (casa sozinho pela categoria)', value: 'empresa' },
                { label: 'Um por veículo/sócio/imóvel (só fecha anexando)', value: 'instancia' },
              ]} />
          </div>
          {f.escopo === 'instancia' && (
            <div style={{ flex: 1, minWidth: 200 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>De qual? (placa, nome, endereço)</Text>
              <Input value={f.chave_instancia} onChange={(e) => set('chave_instancia', e.target.value)}
                placeholder="HILUX-ABC1D34" />
            </div>
          )}
        </Flex>
        <Flex gap={16} wrap align="center">
          <Space size={6}>
            <Switch checked={f.obrigatorio} onChange={(v) => set('obrigatorio', v)} size="small" />
            <Text style={{ fontSize: 12 }}>Obrigatório</Text>
          </Space>
          <Space size={6}>
            <Switch checked={f.exige_validade} onChange={(v) => set('exige_validade', v)} size="small" />
            <Tooltip title="Certidão vence; contrato social não. Sem isso, documento permanente apareceria eternamente como pendência.">
              <Text style={{ fontSize: 12 }}>Exige data de validade</Text>
            </Tooltip>
          </Space>
          <Space size={6}>
            <Text style={{ fontSize: 12 }}>Avisar</Text>
            <InputNumber size="small" min={0} max={365} style={{ width: 76 }}
              value={f.alerta_dias} onChange={(v) => set('alerta_dias', v ?? 30)} />
            <Text style={{ fontSize: 12 }}>dias antes</Text>
          </Space>
          <Space size={6}>
            <Text style={{ fontSize: 12 }}>Quantidade</Text>
            <InputNumber size="small" min={1} max={99} style={{ width: 64 }}
              value={f.quantidade_esperada} onChange={(v) => set('quantidade_esperada', v ?? 1)} />
          </Space>
        </Flex>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Observação (opcional)</Text>
          <Input.TextArea rows={2} value={f.descricao} onChange={(e) => set('descricao', e.target.value)} />
        </div>
      </Flex>
    </Modal>
  );
}
